import { Box, Popover, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { AutoTooltip } from '@/components/tooltips/autoTooltip';
import { fromDaysToBiggestAsPossible } from '@/utils/dateTime';

import { RetentionSelector } from './retentionSelector';

interface RetentionPopoverProps {
  initialRetentionInDays: number;
  minimumRetentionInDays?: number;
  onUpdate: (retentionInDays: number) => void;
  popoverTitle?: string;
}

export const getHumanizeRetentionText = (retentionInDays: number) => {
  const formattedRetention = fromDaysToBiggestAsPossible(retentionInDays);
  const retentionText =
    formattedRetention.value === 1
      ? `a ${formattedRetention.unit.slice(0, -1)}`
      : `${formattedRetention.value} ${formattedRetention.unit}`;
  return retentionText;
};

export const RetentionPopover = (props: RetentionPopoverProps) => {
  const {
    initialRetentionInDays,
    minimumRetentionInDays = 1,
    onUpdate,
  } = props;
  const firstMount = useRef(true);
  const prev = useRef(minimumRetentionInDays);
  const [minValueTooltipOpen, setMinValueTooltipOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const updateRetention = (updatedRetention: number) => {
    const backupRetention = Math.max(updatedRetention, minimumRetentionInDays);
    onUpdate(backupRetention);
  };

  const formattedRetention = fromDaysToBiggestAsPossible(
    initialRetentionInDays
  );

  useEffect(() => {
    // Skip initial mount (including StrictMode’s double-mount)
    if (firstMount.current) {
      firstMount.current = false;
      prev.current = minimumRetentionInDays;
      return;
    }

    // Only run when value actually changes
    if (!Object.is(prev.current, minimumRetentionInDays)) {
      prev.current = minimumRetentionInDays;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMinValueTooltipOpen(true);
    }
  }, [minimumRetentionInDays]);

  return (
    <>
      <AutoTooltip
        open={minValueTooltipOpen}
        onOpenChange={setMinValueTooltipOpen}
        title='Retention changed to match the new frequency.'
      >
        <ButtonWithIcon
          ref={setAnchorEl}
          text={getHumanizeRetentionText(initialRetentionInDays)}
          icon='material-symbols-arrow-drop-down-rounded'
          iconPosition='right'
          onClick={() => setPopoverOpen(true)}
          data-testid='create-backup-policy-retention-select'
          disableFocusRipple
        />
      </AutoTooltip>
      <Popover
        disablePortal
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setPopoverOpen(false)}
      >
        <Box className='flex flex-col p-[24px] gap-[24px] w-[300px]'>
          <Typography variant='subtitle1'>
            {props.popoverTitle || 'Choose a retention time'}
          </Typography>
          <RetentionSelector
            initialFragment={formattedRetention.unit}
            initialRetentionDays={initialRetentionInDays}
            onChange={updateRetention}
          />
        </Box>
      </Popover>
    </>
  );
};
